import React, { useState } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../services/firebase';
import { MessageSquare, X, Send, Camera, Bug, Lightbulb, MessageCircle, CheckCircle, Loader } from 'lucide-react';

const FeedbackButton = ({ user, currentPage = 'unknown', clubId = null, clubName = null }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [feedbackType, setFeedbackType] = useState('');
    const [description, setDescription] = useState('');
    const [screenshot, setScreenshot] = useState(null);
    const [screenshotPreview, setScreenshotPreview] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const feedbackTypes = [
        { value: 'bug', label: 'Bug Report', icon: Bug, color: 'text-red-500', description: 'Something isn\'t working' },
        { value: 'feature', label: 'Feature Request', icon: Lightbulb, color: 'text-yellow-500', description: 'Suggest an improvement' },
        { value: 'general', label: 'Club Feedback', icon: MessageCircle, color: 'text-blue-500', description: 'Feedback for your club admins' }
    ];

    const handleScreenshotChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setScreenshot(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setScreenshotPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!feedbackType || !description.trim()) {
            alert('Please select a feedback type and provide a description');
            return;
        }

        setSubmitting(true);

        try {
            let screenshotURL = null;

            // Upload screenshot if provided
            if (screenshot) {
                const screenshotRef = ref(storage, `feedback/${user.uid}/${Date.now()}_${screenshot.name}`);
                await uploadBytes(screenshotRef, screenshot);
                screenshotURL = await getDownloadURL(screenshotRef);
            }

            // Save feedback to Firestore
            const feedbackRef = collection(db, 'feedback');
            await addDoc(feedbackRef, {
                type: feedbackType,
                description: description.trim(),
                screenshotURL,
                // User info
                userId: user.uid,
                userEmail: user.email,
                userName: user.displayName || user.email?.split('@')[0] || 'Unknown',
                // Club info (for general/club feedback)
                clubId: feedbackType === 'general' ? clubId : null,
                clubName: feedbackType === 'general' ? clubName : null,
                // Context
                currentPage,
                userAgent: navigator.userAgent,
                screenSize: `${window.innerWidth}x${window.innerHeight}`,
                // Metadata
                status: 'new', // new, in-progress, resolved, closed
                createdAt: new Date().toISOString(),
                resolvedAt: null,
                adminNotes: null
            });

            setSubmitted(true);

            // Reset after showing success
            setTimeout(() => {
                setIsOpen(false);
                setSubmitted(false);
                setFeedbackType('');
                setDescription('');
                setScreenshot(null);
                setScreenshotPreview(null);
            }, 2000);

        } catch (error) {
            console.error('Error submitting feedback:', error);
            alert('Failed to submit feedback. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        setIsOpen(false);
        // Reset form when closing
        setTimeout(() => {
            setFeedbackType('');
            setDescription('');
            setScreenshot(null);
            setScreenshotPreview(null);
            setSubmitted(false);
        }, 300);
    };

    if (!user) return null;

    return (
        <>
            {/* Floating Feedback Button */}
            <button
                onClick={() => setIsOpen(true)}
                className="fixed bottom-6 right-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 z-40 group"
                title="Send Feedback"
            >
                <MessageSquare size={24} />
                <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-sm px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    Send Feedback
                </span>
            </button>

            {/* Feedback Modal */}
            {isOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
                        {/* Header */}
                        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-2xl">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h2 className="text-2xl font-bold">Send Feedback</h2>
                                    <p className="text-blue-100 mt-1">Help us improve ClubHudl</p>
                                </div>
                                <button
                                    onClick={handleClose}
                                    className="text-white/80 hover:text-white transition-colors"
                                >
                                    <X size={24} />
                                </button>
                            </div>
                        </div>

                        {/* Success State */}
                        {submitted ? (
                            <div className="p-8 text-center">
                                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <CheckCircle className="text-green-600" size={32} />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h3>
                                <p className="text-gray-600">Your feedback has been submitted successfully.</p>
                            </div>
                        ) : (
                            /* Form */
                            <form onSubmit={handleSubmit} className="p-6 space-y-6">
                                {/* Feedback Type Selection */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                                        What type of feedback do you have?
                                    </label>
                                    <div className="grid grid-cols-1 gap-3">
                                        {feedbackTypes.map((type) => {
                                            const Icon = type.icon;
                                            const isSelected = feedbackType === type.value;
                                            return (
                                                <button
                                                    key={type.value}
                                                    type="button"
                                                    onClick={() => setFeedbackType(type.value)}
                                                    className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${isSelected
                                                            ? 'border-blue-500 bg-blue-50'
                                                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                                        }`}
                                                >
                                                    <div className={`p-2 rounded-lg ${isSelected ? 'bg-blue-100' : 'bg-gray-100'}`}>
                                                        <Icon className={type.color} size={24} />
                                                    </div>
                                                    <div>
                                                        <p className={`font-semibold ${isSelected ? 'text-blue-700' : 'text-gray-800'}`}>
                                                            {type.label}
                                                        </p>
                                                        <p className="text-sm text-gray-500">{type.description}</p>
                                                    </div>
                                                    {isSelected && (
                                                        <div className="ml-auto">
                                                            <CheckCircle className="text-blue-500" size={20} />
                                                        </div>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Description */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Tell us more <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        placeholder={
                                            feedbackType === 'bug'
                                                ? 'Please describe what happened, what you expected, and steps to reproduce...'
                                                : feedbackType === 'feature'
                                                    ? 'Describe the feature you\'d like to see and how it would help you...'
                                                    : 'Share feedback with your club admins (suggestions, concerns, ideas)...'
                                        }
                                        rows={4}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                        required
                                    />
                                </div>

                                {/* Screenshot Upload */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Add a screenshot (optional)
                                    </label>

                                    {screenshotPreview ? (
                                        <div className="relative">
                                            <img
                                                src={screenshotPreview}
                                                alt="Screenshot preview"
                                                className="w-full h-40 object-cover rounded-xl border border-gray-200"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setScreenshot(null);
                                                    setScreenshotPreview(null);
                                                }}
                                                className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600"
                                            >
                                                <X size={16} />
                                            </button>
                                        </div>
                                    ) : (
                                        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:bg-gray-50 hover:border-gray-400 transition-colors">
                                            <Camera className="text-gray-400 mb-2" size={24} />
                                            <span className="text-sm text-gray-500">Click to upload screenshot</span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleScreenshotChange}
                                                className="hidden"
                                            />
                                        </label>
                                    )}
                                </div>

                                {/* User Info Display */}
                                <div className="bg-gray-50 rounded-xl p-4">
                                    <p className="text-xs text-gray-500 mb-1">Submitting as</p>
                                    <p className="font-medium text-gray-800">{user.email}</p>
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={submitting || !feedbackType || !description.trim()}
                                    className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader className="animate-spin" size={20} />
                                            Submitting...
                                        </>
                                    ) : (
                                        <>
                                            <Send size={20} />
                                            Submit Feedback
                                        </>
                                    )}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default FeedbackButton;